/**
 * StoreLocalStorage Middleware
 * 
 * This middlware will persist any changes made to the configured stores and save them inside 
 * the users localStorage or sessionStorage under the configured appName. This middlware will also 
 * rehydrate any configured store automatically from local or sessionStorage upon startup.
 * 
 * SETUP
 * 
 * Create a config file that is loaded somewhere in your app.
 * Import the middleware config functions
 * @example import { addToLocalStorage,setStorageConfig } from "@@/middleware/stores/StoreLocalStorage";
 * 
 * Make sure to include the config file somewhere in your application hiearchy
 * /src/index.js or src/app.js is a good place.
 * @example import "@@/config/StoresConfig"
 * 
 * Set the application name under which local or sessionStorage will persist  your data.
 * @example setStorageConfig({appName:"My_APP"})
 * 
 * Apply the correct middleware you want to use and pass in an array of stores.
 * @example addToLocalStorage([<Store>]);
 * @example addToSessionStorage([<Store>]);
 */

const boldCSS = "font-weight:bold";
const actionCSS = "color:#87A7DB;font-weight:bold";
const titleCSS = "color:#d65ca1;font-weight:lighter";
const _localStorage = {};
const _sessionStorage = {};
const DEFAULT_APP_NAME = "SELKIRK";
let _config = {
  appName: DEFAULT_APP_NAME
};
const getName = name => `${_config.appName || DEFAULT_APP_NAME}.${name}`;
const byteCount = s => {
  return encodeURI(s).split(/%..|./).length - 1;
};
const getMegaByte = bytes => {
  return bytes / 2048;
};
const serializeState = state => {
  const dataStr = JSON.stringify(state);
  const size = getMegaByte(byteCount(dataStr));
  if (size >= 1) {
    console.warn(`Storage Limit 5MB (${size} MB):`, state);
  }
  return dataStr;
};
const saveToLocalStorage = (name, state) => {
  if (_config.appName === DEFAULT_APP_NAME || !_config.appName) {
    console.log(`StoreLocalStorage: appName has not been set, using default ${DEFAULT_APP_NAME}`);
  }
  try {
    window.localStorage.setItem(getName(name), serializeState(state));
  } catch (e) {
    console.error(e);
  }
};
const saveToSessionStorage = (name, state) => {
  if (_config.appName === DEFAULT_APP_NAME || !_config.appName) {
    console.log(`StoreLocalStorage: appName has not been set, using default ${DEFAULT_APP_NAME}`);
  }
  try {
    window.sessionStorage.setItem(getName(name), serializeState(state));
  } catch (e) {
    console.error(e);
  }
};
export const clearLocalStorageByStore = store => {
  localStorage.removeItem(getName(store.__STORAGE_NAME || store.getName()));
};
export const clearSessionStorage = store => {
  sessionStorage.removeItem(getName(store.__STORAGE_NAME || store.getName()));
};
export const setStorageConfig = config => {
  _config = {
    ..._config,
    ...config
  };
};
export const fromStorage = initState => (name, events, storageName = '') => {
  const finalName = getName(`${name}:${storageName}`);
  if (_sessionStorage[name]) {
    try {
      let state = window.sessionStorage.getItem(finalName);
      if (state) {
        state = JSON.parse(state);
        console.groupCollapsed(`%cretrieve %c${name}`, titleCSS, boldCSS);
        console.log("%cSession Storage", actionCSS, state);
        console.groupEnd();
        return {
          ...initState,
          ...state
        };
      }
    } catch (e) {
      console.error(e);
      return initState;
    }
    return initState;
  }
  if (_localStorage[name]) {
    try {
      let state = window.localStorage.getItem(finalName);
      if (state) {
        state = JSON.parse(state);
        console.groupCollapsed(`%cretrieve %c${name}`, titleCSS, boldCSS);
        console.log("%cLocal Storage", actionCSS, state);
        console.groupEnd();
        return {
          ...initState,
          ...state
        };
      }
    } catch (e) {
      console.error(e);
      return initState;
    }
  }
  return initState;
};
export const addToLocalStorage = stores => {
  stores.forEach(s => {
    const name = s.getName();
    _localStorage[name] = s;
    const localState = fromStorage({})(name, undefined, s.__STORAGE_NAME || name);

    //Automatically rehydrate from localStorage if a value exists
    //NOTE: this will override any initial state specified in the store file.
    if (localState) {
      s._setInitialState(localState);
    }
  });
};
export const addToSessionStorage = stores => {
  stores.forEach(s => {
    const name = s.getName();
    const sessionState = fromStorage({})(name, undefined, s.__STORAGE_NAME || name);

    //Automatically rehydrate from sessionStorage if a value exists
    //NOTE: this will override any initial state specified in the store file.
    if (sessionState) {
      s._setInitialState(sessionState);
    }
    _sessionStorage[s.getName()] = s;
  });
};
export const getStoreName = (store, name) => {
  return `${store.getName()}:${store.__STORAGE_NAME ? store.__STORAGE_NAME : name}`;
};
export const getLocalStorage = name => {
  try {
    let state = window.localStorage.getItem(getName(name));
    if (state) {
      state = JSON.parse(state);
      return state;
    }
  } catch (e) {
    console.error(e);
    return null;
  }
};
export const getSessionStorage = name => {
  try {
    let state = window.sessionStorage.getItem(getName(name));
    if (state) {
      state = JSON.parse(state);
      return state;
    }
  } catch (e) {
    console.error(e);
    return null;
  }
};
export const localStorageMiddleware = name => events => action => state => next => {
  let nextState = next(name, events, action, state);
  if (nextState === state) return nextState;
  if (_sessionStorage.hasOwnProperty(name)) {
    const store = _sessionStorage[name];
    const storageName = getStoreName(store, name);

    //TODO: add size to console group in MB
    console.groupCollapsed(`%cpersist %c${storageName}`, titleCSS, boldCSS);
    console.log("%cSession Storage", actionCSS, nextState);
    console.groupEnd();
    saveToSessionStorage(storageName, nextState);
  }
  if (_localStorage.hasOwnProperty(name)) {
    const store = _localStorage[name];
    const storageName = getStoreName(store, name);
    console.groupCollapsed(`%cpersist %c${storageName}`, titleCSS, boldCSS);
    console.log("%cLocal Storage", actionCSS, nextState);
    console.groupEnd();
    saveToLocalStorage(storageName, nextState);
  }
  return nextState;
};